import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { apiService } from '@/lib/api';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { ChargeConfirmModal } from './ChargeConfirmModal';
import type { BillingPlan, BillingChargePreview } from '@/types';

const FULL_PLANS_URL = 'https://www.servicestrike.com/pricing.html';

function usd(cents: number): string {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function quotaLine(n: number, singular: string, pluralWord: string): string {
    if (n === 0) return `${pluralWord} can be purchased`;
    return `${n} included ${n === 1 ? singular : pluralWord} (more can be purchased)`;
}

const SOLO_FEATURES = [
    'Scheduling',
    'Estimates',
    'Branded Invoices',
    'Mobile App',
    'Photos and Docs Upload',
    'Job Cost Calculator',
    'Payments',
];

const STARTER_FEATURES = [
    'Permission Management',
    'Custom Reports',
    'Items Catalog',
    'Team Chat',
];

const PRO_FEATURES = [
    'Team GPS Tracking',
    'Call/SMS Masking',
    'Call Recording',
    'Custom Fields',
];

const PREMIUM_FEATURES = [
    'Multi Company Management',
    'Client Review Funnel',
    'Multi Team Projects',
    'Priority Support',
];

function planDetailLines(plan: BillingPlan): string[] {
    const lines: string[] = [
        plan.included_seats === 1
            ? '1 User'
            : `${plan.included_seats} included users (more can be purchased)`,
    ];
    if (plan.included_companies > 1) {
        lines.push(`${plan.included_companies} included companies`);
    }
    if (plan.key === 'solo') {
        lines.push(...SOLO_FEATURES);
        lines.push(quotaLine(plan.included_phone_numbers, 'Phone number', 'Phone numbers'));
        return lines;
    }
    lines.push(quotaLine(plan.included_phone_numbers, 'Phone number', 'Phone numbers'));
    if (plan.included_sms_segments > 0) {
        lines.push(quotaLine(plan.included_sms_segments, 'SMS', 'SMS'));
    }
    if (plan.included_voice_minutes > 0) {
        lines.push(quotaLine(plan.included_voice_minutes, 'minute', 'minutes'));
    }
    if (plan.key === 'starter') {
        lines.push('Everything in Solo, plus:');
        lines.push(...STARTER_FEATURES);
    }
    if (plan.key === 'pro') {
        lines.push('Everything in Starter, plus:');
        lines.push(...PRO_FEATURES);
    }
    if (plan.key === 'premium') {
        lines.push('Everything in Pro, plus:');
        lines.push(...PREMIUM_FEATURES);
    }
    return lines;
}

interface PlanPickerProps {
    /** Called after a re-subscribe activated immediately (card already on file). */
    onActivated?: (charged: BillingChargePreview) => void;
    /** Highlighted as the current plan and made non-selectable. */
    currentPlanKey?: string | null;
    /**
     * When set, selecting a plan calls this instead of starting a new subscription — used by the
     * "change plan" flow for an already-active org (which must not re-anchor or re-checkout).
     */
    onSelectPlan?: (planKey: string, period: 'Monthly' | 'Annual', charged: BillingChargePreview) => Promise<void>;
    /** Which charge-preview action to quote. Default subscribe. Pass change_plan for an active org's picker. */
    chargeAction?: 'subscribe' | 'change_plan';
}

/**
 * Catalog plan picker with a monthly/annual toggle. By default (subscribe flow) selecting a plan
 * starts checkout: if a card is on file it activates immediately (onActivated), otherwise it
 * redirects to Stripe Checkout. When onSelectPlan is provided (change-plan flow) that runs instead.
 * Web-only — callers must not render it on native.
 */
export function PlanPicker({ onActivated, currentPlanKey, onSelectPlan, chargeAction = 'subscribe' }: PlanPickerProps) {
    const [period, setPeriod] = useState<'Monthly' | 'Annual'>('Monthly');
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState<{ key: string; period: 'Monthly' | 'Annual' } | null>(null);

    const { data: plans, isLoading } = useQuery({
        queryKey: ['billing-plans'],
        queryFn: () => apiService.getBillingPlans(),
        staleTime: 5 * 60_000,
    });

    const sorted = useMemo(
        () => (plans ?? []).slice().sort((a, b) => a.monthly_price_cents - b.monthly_price_cents),
        [plans],
    );

    const handleSelect = (plan: BillingPlan) => {
        setError(null);
        setPending({ key: plan.key, period });
    };

    const confirmPending = async (charged: BillingChargePreview) => {
        if (!pending) return;
        setBusy(pending.key);
        try {
            if (onSelectPlan) {
                await onSelectPlan(pending.key, pending.period, charged);
                setBusy(null);
                return;
            }
            const result = await apiService.subscribe(pending.key, pending.period);
            if (result.checkout_url) {
                window.location.href = result.checkout_url;
                return;
            }
            if (result.activated) {
                setBusy(null);
                onActivated?.(charged);
                return;
            }
            setError('Could not start checkout (no payment page was returned). Check the billing configuration.');
            setBusy(null);
        } catch (e) {
            const apiError = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
            setError(apiError ?? (e instanceof Error ? e.message : 'Something went wrong. Please try again.'));
            setBusy(null);
            throw e; // keep the confirm modal open
        }
    };

    if (isLoading) {
        return <div className="text-center py-8"><LoadingSpinner /></div>;
    }

    return (
        <div className="space-y-4">
            {/* Monthly/Annual toggle only in the subscribe flow. Self-service change-plan is
                Monthly-only (annual and monthly↔annual changes are operator-handled and the API
                rejects them), so offering the toggle here would just produce a 400. */}
            {!onSelectPlan && (
            <div className="flex items-center justify-center gap-2">
                {(['Monthly', 'Annual'] as const).map((p) => (
                    <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            period === p ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                        {p}
                        {p === 'Annual' && <span className="ml-1 text-xs opacity-80">(save with annual)</span>}
                    </button>
                ))}
            </div>
            )}

            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                    {error}
                </div>
            )}

            {/* Subscribe flow (no onSelectPlan) sends the user to Stripe Checkout to enter a card. */}
            {!onSelectPlan && (
                <p className="text-center text-sm text-gray-500">
                    If you don't have a card on file yet, you'll be taken to our payment page to enter it.
                </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {sorted.map((plan) => {
                    const isCurrent = plan.key === currentPlanKey;
                    const priceCents = period === 'Annual' ? plan.annual_price_cents : plan.monthly_price_cents;
                    return (
                        <div
                            key={plan.key}
                            className={`rounded-lg border p-4 flex flex-col ${
                                isCurrent ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-gray-900">{plan.display_name}</h4>
                                {isCurrent && <span className="text-xs text-blue-600 font-medium">Current</span>}
                            </div>
                            <div className="mt-2">
                                <span className="text-2xl font-bold text-gray-900">{usd(priceCents)}</span>
                                <span className="text-sm text-gray-500">/{period === 'Annual' ? 'yr' : 'mo'}</span>
                            </div>
                            <ul className="mt-3 space-y-1 text-sm text-gray-600 flex-1">
                                {planDetailLines(plan).map((line) => {
                                    const heading = line.endsWith(', plus:');
                                    return (
                                        <li key={line} className={`flex items-start gap-1.5 ${heading ? 'mt-1.5 font-medium text-gray-700' : ''}`}>
                                            {!heading && <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />}
                                            {line}
                                        </li>
                                    );
                                })}
                            </ul>
                            <a
                                href={FULL_PLANS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 text-sm text-blue-600 hover:underline"
                            >
                                See full plan details
                            </a>
                            <button
                                disabled={isCurrent || busy !== null}
                                onClick={() => handleSelect(plan)}
                                className="mt-4 w-full py-2 rounded-md bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
                            >
                                {busy === plan.key ? 'Starting…' : isCurrent ? 'Current plan' : 'Choose plan'}
                            </button>
                        </div>
                    );
                })}
            </div>

            <ChargeConfirmModal
                open={pending !== null}
                planKey={pending?.key ?? null}
                billingPeriod={pending?.period ?? period}
                action={chargeAction}
                onClose={() => setPending(null)}
                onConfirm={confirmPending}
            />
        </div>
    );
}
